import { Befehl25FormData, Befehl25FormSchema } from './schema'
import { Befehl25 } from '../../../models'
import { Betriebsstellenart } from '../../../types'
import {
  Befehl25DataDto,
  BefehlAuftrag_25_10Dto,
  BefehlAuftrag_25_20Dto,
  BefehlAuftrag_25_30Dto,
  BefehlAuftrag_25_34Dto
} from '../../../dtos/befehl-25-dto'
import { BefehlFeldDto } from '../../../dtos'
import { findAuftragByKennung, hasAuftrag } from '../converter-utils'

export const convertModelToBefehl25FormData = (
  befehl?: Befehl25
): Befehl25FormData => {
  const formData = Befehl25FormSchema.parse({})

  if (!befehl?.auftraege) {
    return formData
  }

  // Set enabled based on what's in the model
  if (hasAuftrag(befehl.auftraege, '25.10')) {
    formData.auftrag_25_10.enabled = true
  }
  if (hasAuftrag(befehl.auftraege, '25.20')) {
    formData.auftrag_25_20.enabled = true
  }
  if (hasAuftrag(befehl.auftraege, '25.30')) {
    formData.auftrag_25_30.enabled = true
  }
  if (hasAuftrag(befehl.auftraege, '25.34')) {
    formData.auftrag_25_34.enabled = true
  }

  // Extract field data for each auftrag type
  const extractAuftragData = (
    auftrag:
      | BefehlAuftrag_25_10Dto
      | BefehlAuftrag_25_20Dto
      | BefehlAuftrag_25_30Dto
      | BefehlAuftrag_25_34Dto,
    formField: { km: string; selector: string; name: string },
    kmFieldKey: string,
    selectorMappings: Array<{
      feldKey: string
      type: Betriebsstellenart
      valueKey: string
    }>
  ) => {
    for (const feld of auftrag.felder) {
      if (feld.kennung === kmFieldKey) {
        formField.km = feld.werte.km
      } else {
        const mapping = selectorMappings.find((m) => feld.kennung === m.feldKey)
        if (mapping) {
          formField.selector = mapping.type
          formField.name = feld.werte[mapping.valueKey]
        }
      }
    }
  }

  const auftrag25_10 = findAuftragByKennung(befehl.auftraege, '25.10')
  if (auftrag25_10) {
    extractAuftragData(auftrag25_10, formData.auftrag_25_10, '25.11', [
      { feldKey: '25.12', type: Betriebsstellenart.Abzw, valueKey: 'abzw' },
      { feldKey: '25.13', type: Betriebsstellenart.Uest, valueKey: 'uest' }
    ])
  }

  const auftrag25_20 = findAuftragByKennung(befehl.auftraege, '25.20')
  if (auftrag25_20) {
    extractAuftragData(auftrag25_20, formData.auftrag_25_20, '25.21', [
      { feldKey: '25.22', type: Betriebsstellenart.Abzw, valueKey: 'abzw' },
      { feldKey: '25.23', type: Betriebsstellenart.Uest, valueKey: 'uest' }
    ])
  }

  const auftrag25_30 = findAuftragByKennung(befehl.auftraege, '25.30')
  if (auftrag25_30) {
    extractAuftragData(auftrag25_30, formData.auftrag_25_30, '25.31', [
      { feldKey: '25.32', type: Betriebsstellenart.Bf, valueKey: 'bf' },
      { feldKey: '25.33', type: Betriebsstellenart.Bft, valueKey: 'bft' }
    ])
  }

  return formData
}

export const convertBefehl25FormDataToModel = (
  formData: Befehl25FormData,
  frontendId: string
): Befehl25 => {
  const auftraege: Befehl25DataDto = []

  // Create auftrag fields
  const createAuftragFields = (
    kmField: string,
    kmValue: string,
    selector: string,
    name: string,
    selectorMappings: Array<{
      type: Betriebsstellenart
      feldKey: string
      valueKey: string
    }>
  ): BefehlFeldDto[] => {
    const fields: BefehlFeldDto[] = [
      {
        kennung: kmField,
        werte: { km: kmValue || '' }
      }
    ]

    const mapping = selectorMappings.find((m) => m.type === selector)
    if (mapping) {
      fields.push({
        kennung: mapping.feldKey,
        werte: { [mapping.valueKey]: name || '' }
      } as BefehlFeldDto)
    }

    return fields
  }

  if (formData.auftrag_25_10?.enabled) {
    const fields = createAuftragFields(
      '25.11',
      formData.auftrag_25_10?.km,
      formData.auftrag_25_10?.selector,
      formData.auftrag_25_10?.name,
      [
        { type: Betriebsstellenart.Abzw, feldKey: '25.12', valueKey: 'abzw' },
        { type: Betriebsstellenart.Uest, feldKey: '25.13', valueKey: 'uest' }
      ]
    )

    auftraege.push({
      kennung: '25.10',
      felder: fields
    } as BefehlAuftrag_25_10Dto)
  }

  if (formData.auftrag_25_20?.enabled) {
    const fields = createAuftragFields(
      '25.21',
      formData.auftrag_25_20?.km,
      formData.auftrag_25_20?.selector,
      formData.auftrag_25_20?.name,
      [
        { type: Betriebsstellenart.Abzw, feldKey: '25.22', valueKey: 'abzw' },
        { type: Betriebsstellenart.Uest, feldKey: '25.23', valueKey: 'uest' }
      ]
    )

    auftraege.push({
      kennung: '25.20',
      felder: fields
    } as BefehlAuftrag_25_20Dto)
  }

  if (formData.auftrag_25_30?.enabled) {
    const fields = createAuftragFields(
      '25.31',
      formData.auftrag_25_30?.km,
      formData.auftrag_25_30?.selector,
      formData.auftrag_25_30?.name,
      [
        { type: Betriebsstellenart.Bf, feldKey: '25.32', valueKey: 'bf' },
        { type: Betriebsstellenart.Bft, feldKey: '25.33', valueKey: 'bft' }
      ]
    )

    auftraege.push({
      kennung: '25.30',
      felder: fields
    } as BefehlAuftrag_25_30Dto)
  }

  if (formData.auftrag_25_34?.enabled) {
    auftraege.push({
      kennung: '25.34',
      felder: []
    })
  }

  return {
    kennung: '25',
    frontendId,
    auftraege
  }
}
